/**
 * Notification Types for Advisor Panel
 */

export type NotificationType =
  | "new_lead"
  | "session_started"
  | "session_message"
  | "session_ended"
  | "credits_assigned"
  | "system"
  | "info"
  | "warning"
  | "error";

export type NotificationPriority = "low" | "normal" | "high" | "urgent";

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  priority: NotificationPriority;
  timestamp: string;
  read: boolean;
  /** Optional link to navigate to when clicked */
  link?: string;
  /** Optional metadata for the notification */
  metadata?: Record<string, unknown>;
  /** Auto-dismiss after this many milliseconds (0 = no auto-dismiss) */
  autoDismissMs?: number;
}

export interface NotificationSettings {
  /** Enable browser push notifications */
  browserPushEnabled: boolean;
  /** Enable sound for notifications */
  soundEnabled: boolean;
  /** Enable desktop notifications */
  desktopEnabled: boolean;
  /** Which notification types to show */
  enabledTypes: NotificationType[];
  /** Quiet hours - don't show notifications */
  quietHours?: {
    enabled: boolean;
    from: string; // HH:mm format
    to: string;
  };
}

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  browserPushEnabled: true,
  soundEnabled: true,
  desktopEnabled: true,
  enabledTypes: [
    "new_lead",
    "session_started",
    "session_message",
    "session_ended",
    "credits_assigned",
    "system",
    "warning",
    "error",
  ],
  quietHours: {
    enabled: false,
    from: "22:00",
    to: "08:00",
  },
};

export const NOTIFICATION_ICONS: Record<NotificationType, string> = {
  new_lead: "UserPlus",
  session_started: "MessageSquare",
  session_message: "MessageCircle",
  session_ended: "CheckCircle",
  credits_assigned: "Coins",
  system: "Settings",
  info: "Info",
  warning: "AlertTriangle",
  error: "XCircle",
};

export const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  new_lead: "text-blue-600 bg-blue-100",
  session_started: "text-green-600 bg-green-100",
  session_message: "text-indigo-600 bg-indigo-100",
  session_ended: "text-gray-600 bg-gray-100",
  credits_assigned: "text-yellow-600 bg-yellow-100",
  system: "text-gray-600 bg-gray-100",
  info: "text-blue-600 bg-blue-100",
  warning: "text-orange-600 bg-orange-100",
  error: "text-red-600 bg-red-100",
};
